import React, { ComponentType, useMemo, useState } from 'react';
import { RenderItemProps, VirtualList } from '@shared/components/ui/VirtualList';
import { useTranslation } from 'react-i18next';
import { RulesModel } from '../../models/Rules';
import { Card, CardProps } from './Card';
import { Button } from '@shared/components/ui/button';
import {
  DndContext,
  DragOverlay,
  useSensors,
  useSensor,
  MouseSensor,
  TouchSensor,
  MeasuringStrategy,
  rectIntersection,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RulesApiAdapter } from '../../adapters/RulesApiAdapter';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useToast } from '@shared/hooks/use-toast';

// CardList.tsx
// Componente que muestra las reglas de envío en formato de tarjetas (usado en mobile o vistas alternativas).

// 🎯 Interface Segregation Principle (ISP) - Mismas props que RulesTable
interface CardListProps {
  rules: RulesModel[];
  isLoading?: boolean;
  className?: string;
  // 🎯 Props para scroll infinito
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  // 🎯 Props para sorting - manejado desde el padre
  sorting?: any;
  onSortingChange?: (sorting: any) => void;
  total?: number;
  // 🎯 Props específicas para cards
  showActions?: boolean;
  // 🎯 Prop para estimación de altura cuando es dinámica
  estimateItemHeight?: number;
  onEditRule?: (rule: RulesModel) => void;
  onDeleteRule?: (rule: RulesModel) => void;
  onToggleRule?: (rule: RulesModel, activate: boolean) => void;
  onReorder?: (newOrder: RulesModel[]) => void; // 🎯 Agregar onReorder a la interfaz
  /** team_id del cliente hijo para PUT /shipping-rules/reorder (3PL, ruta /clients/:id/rules) */
  reorderTeamId?: number;
  isReadOnly?: boolean; // 🎯 Flag para modo solo lectura
  canEdit?: boolean;
  canDelete?: boolean;
  /** Sustituye la tarjeta por defecto (p. ej. reglas de cliente con otro diseño) */
  CardComponent?: ComponentType<CardProps>;
  /**
   * Rellena un contenedor flex con altura fija: la lista aporta un único scroll
   * (evita doble barra con el `overflow-y-auto` del layout global).
   */
  fillContainer?: boolean;
  /**
   * `immediate` = al soltar, se persiste en API (vista móvil reglas generales).
   * `onSave` = reordenar en local; Guardar / Descartar como en zonas de cobertura (reglas de cliente).
   * `local` = al soltar, notifica al padre vía `onReorder` sin llamar a la API (p. ej. borrador de preset).
   */
  reorderMode?: 'immediate' | 'onSave' | 'local';
  /** Altura dinámica según cantidad de ítems (sin viewport fijo). */
  autoHeight?: boolean;
  /** p. ej. búsqueda activa: no arrastrar ni guardar orden (como prioridad estable al filtrar). */
  reorderDisabled?: boolean;
  /** Tras guardar el orden (solo `reorderMode: 'onSave'`) con éxito: p. ej. remontar la lista. */
  onReorderSaveSuccess?: () => void;
}

// Componente SortableCard para envolver cada Card con lógica de drag
const SortableCard = ({ id, children }: { id: string | number; children: (dragProps: any) => React.ReactNode }) => {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners })}
    </div>
  );
};

export const CardList = ({
  rules,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  sorting,
  onSortingChange,
  total,
  className = '',
  estimateItemHeight = 168,
  onEditRule,
  onDeleteRule,
  onToggleRule,
  onReorder,
  reorderTeamId,
  isReadOnly = false, // 🎯 Default a false
  canEdit = true,
  canDelete = true,
  CardComponent = Card,
  fillContainer = false,
  reorderMode = 'immediate',
  reorderDisabled = false,
  onReorderSaveSuccess,
  autoHeight = false,
}: CardListProps) => {
  const { t } = useTranslation('rulesModule');
  const isOnSave = reorderMode === 'onSave';
  const isLocal = reorderMode === 'local';
  const allowDrag = !isReadOnly && canEdit && !reorderDisabled;
  const onReorderSaveSuccessRef = React.useRef(onReorderSaveSuccess);
  React.useEffect(() => {
    onReorderSaveSuccessRef.current = onReorderSaveSuccess;
  }, [onReorderSaveSuccess]);
  const [orderedRules, setOrderedRules] = useState(rules);
  const [queryEnabled, setQueryEnabled] = useState(true);
  const [activeId, setActiveId] = useState<string | number | null>(null);
  const [initialRulesOnDrag, setInitialRulesOnDrag] = useState<RulesModel[] | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const rulesRef = React.useRef(rules);
  const reorderModeRef = React.useRef(reorderMode);
  const localOrderPendingRef = React.useRef(false);
  React.useEffect(() => {
    rulesRef.current = rules;
  }, [rules]);
  React.useEffect(() => {
    reorderModeRef.current = reorderMode;
  }, [reorderMode]);

  const hasUnsavedOrder = useMemo(() => {
    if (reorderDisabled || isReadOnly || !isOnSave || !canEdit) return false;
    if (rules.length === 0) return false;
    return JSON.stringify(orderedRules.map((r) => r.id)) !== JSON.stringify(rules.map((r) => r.id));
  }, [reorderDisabled, isReadOnly, isOnSave, canEdit, orderedRules, rules]);

  React.useEffect(() => {
    if (!reorderDisabled) return;
    setOrderedRules(rules);
    setQueryEnabled(true);
    setInitialRulesOnDrag(null);
    setActiveId(null);
  }, [reorderDisabled, rules]);

  // 🚀 Adapter + mutación para reordenar (igual que en Table)
  const api = React.useMemo(() => new RulesApiAdapter(createHttpClient(apiClientWH)), []);

  const reorderMutation = useMutation({
    mutationFn: (ids: number[]) => api.reorder(ids, reorderTeamId),
    onError: (err: any) => {
      if (reorderModeRef.current === 'onSave') {
        setOrderedRules(rulesRef.current);
      } else if (initialRulesOnDrag) {
        setOrderedRules(initialRulesOnDrag);
      }
      toast({
        title: t('toast.reorderError'),
        description: err?.message || t('toast.reorderErrorDescription'),
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: t('toast.reorderSuccess'),
        description: t('toast.reorderSuccessDescription'),
        variant: 'default',
      });
    },
    onSettled: async (_data, error) => {
      setInitialRulesOnDrag(null); // Cleanup
      await queryClient.invalidateQueries({ queryKey: ['rules'] });
      setQueryEnabled(true); // Re-enable external updates
      if (!error && reorderModeRef.current === 'onSave') {
        onReorderSaveSuccessRef.current?.();
      }
    },
  });

  // 🎯 Sensores para mejorar drag en mobile (long-press) y pointer
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 120,
        tolerance: 10,
      },
    }),
  );

  // Si cambian las reglas desde fuera, reseteamos el orden local SOLO si queryEnabled
  React.useEffect(() => {
    if (isLocal && localOrderPendingRef.current) {
      setOrderedRules((prev) => {
        const prevIds = prev.map((r) => r.id).join(',');
        const nextIds = rules.map((r) => r.id).join(',');
        if (prevIds !== nextIds) return prev;
        localOrderPendingRef.current = false;
        return rules;
      });
      return;
    }

    if (queryEnabled) {
      setOrderedRules((prev) => {
        const prevIds = prev.map((r) => r.id).join(',');
        const nextIds = rules.map((r) => r.id).join(',');
        if (prevIds === nextIds) return prev;
        return rules;
      });
    }
  }, [rules, queryEnabled, isLocal]);

  React.useEffect(() => {
    if (isLocal && !localOrderPendingRef.current && !queryEnabled) {
      setQueryEnabled(true);
    }
  }, [rules, queryEnabled, isLocal]);

  const handleDragEnd = () => {
    setActiveId(null);

    if (isLocal) {
      const orderChanged =
        initialRulesOnDrag &&
        JSON.stringify(initialRulesOnDrag.map((r) => r.id)) !== JSON.stringify(orderedRules.map((r) => r.id));
      if (orderChanged) {
        localOrderPendingRef.current = true;
        onReorder?.(orderedRules);
      } else {
        setQueryEnabled(true);
      }
      setInitialRulesOnDrag(null);
      return;
    }

    if (isOnSave) {
      const orderChanged =
        initialRulesOnDrag &&
        JSON.stringify(initialRulesOnDrag.map((r) => r.id)) !== JSON.stringify(orderedRules.map((r) => r.id));
      if (orderChanged) {
        // Mantener queryEnabled en false para no machacar el orden con un refetch
      } else {
        setQueryEnabled(true);
      }
      setInitialRulesOnDrag(null);
      return;
    }

    if (
      initialRulesOnDrag &&
      JSON.stringify(initialRulesOnDrag.map((r) => r.id)) !== JSON.stringify(orderedRules.map((r) => r.id))
    ) {
      reorderMutation.mutate(orderedRules.map((r) => r.id));
    } else {
      setInitialRulesOnDrag(null);
      setQueryEnabled(true);
    }
  };

  const handleSaveOrder = () => {
    reorderMutation.mutate(orderedRules.map((r) => r.id));
  };

  const handleCancelOrder = () => {
    setOrderedRules(rulesRef.current);
    setInitialRulesOnDrag(null);
    setQueryEnabled(true);
  };

  const handleDragOver = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setOrderedRules((items) => {
      const oldIndex = items.findIndex((r) => r.id === active.id);
      const newIndex = items.findIndex((r) => r.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return items;
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const handleDragMove = () => {};

  const isReordering = Boolean(!isReadOnly && canEdit && activeId);

  // Render function que usa el componente Card envuelto en SortableCard
  const renderRuleCard = (props: RenderItemProps<RulesModel>) => {
    if (!allowDrag) {
      // 🎯 Para modo solo lectura o sin permiso de edición, renderizar Card sin SortableCard
      return (
        <CardComponent
          {...props}
          onEdit={onEditRule ? () => onEditRule(props.item) : undefined}
          onDelete={onDeleteRule ? () => onDeleteRule(props.item) : undefined}
          isActive={props.item.is_active}
          onToggleActive={() => onToggleRule && onToggleRule(props.item, !props.item.is_active)}
          isReadOnly={isReadOnly} // 🎯 Pasar flag de solo lectura
          canEdit={canEdit}
          canDelete={canDelete}
          isListReordering={false}
        />
      );
    }

    // 🎯 Para modo normal, usar SortableCard
    return (
      <SortableCard id={props.item.id}>
        {({ attributes, listeners }) => (
          <CardComponent
            {...props}
            onEdit={onEditRule ? () => onEditRule(props.item) : undefined}
            onDelete={onDeleteRule ? () => onDeleteRule(props.item) : undefined}
            dragHandleProps={{ ...attributes, ...listeners }}
            isActive={props.item.is_active}
            onToggleActive={() => onToggleRule && onToggleRule(props.item, !props.item.is_active)}
            isReadOnly={isReadOnly} // 🎯 Pasar flag de solo lectura
            canEdit={canEdit}
            canDelete={canDelete}
            isListReordering={isReordering}
          />
        )}
      </SortableCard>
    );
  };

  const getRulesKey = (rules: RulesModel) => rules.id;

  // Encontrar el item activo para el DragOverlay
  const activeItem = activeId ? orderedRules.find((r) => r.id === activeId) : null;
  const activeItemIndex = activeId != null ? orderedRules.findIndex((r) => r.id === activeId) : 0;

  const listHeight = fillContainer ? '100%' : 'calc(100vh - 150px)';

  const listSharedProps = {
    data: orderedRules,
    renderItem: renderRuleCard,
    itemKey: getRulesKey,
    estimateItemHeight,
    height: listHeight,
    autoHeight,
    gap: 0,
    emptyMessage: t('empty.message'),
    className: `rounded-lg shadow-sm ${className}`,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    infiniteScrollThreshold: 300,
    sorting,
    onSortingChange,
    total,
    listClassName: 'touch-pan-y',
    itemClassName: 'touch-manipulation',
  };

  const listShellClass = fillContainer ? 'min-h-0 flex-1' : undefined;

  const inner = allowDrag ? (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      modifiers={[restrictToVerticalAxis]}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={({ active }) => {
        setInitialRulesOnDrag(orderedRules);
        setQueryEnabled(false);
        setActiveId(active.id);
      }}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragMove={handleDragMove}
      onDragCancel={() => {
        if (initialRulesOnDrag) setOrderedRules(initialRulesOnDrag);
        setInitialRulesOnDrag(null);
        localOrderPendingRef.current = false;
        setQueryEnabled(true);
        setActiveId(null);
      }}
    >
      {fillContainer ? (
        <div className="flex h-full min-h-0 w-full flex-col">
          <SortableContext items={orderedRules.map((r) => r.id)} strategy={verticalListSortingStrategy}>
            <div className={listShellClass}>
              <VirtualList {...listSharedProps} />
            </div>
          </SortableContext>
          <DragOverlay>
            {activeItem ? (
              <div className="w-[min(100vw-2rem,520px)]">
                <CardComponent
                  item={activeItem}
                  index={activeItemIndex >= 0 ? activeItemIndex : 0}
                  isLoading={false}
                  onEdit={onEditRule ? () => onEditRule(activeItem) : undefined}
                  onDelete={onDeleteRule ? () => onDeleteRule(activeItem) : undefined}
                  isActive={activeItem.is_active}
                  onToggleActive={() => onToggleRule && onToggleRule(activeItem, !activeItem.is_active)}
                  isListReordering
                  isDragPreview
                />
              </div>
            ) : null}
          </DragOverlay>
        </div>
      ) : (
        <>
          <SortableContext items={orderedRules.map((r) => r.id)} strategy={verticalListSortingStrategy}>
            <VirtualList {...listSharedProps} />
          </SortableContext>
          <DragOverlay>
            {activeItem ? (
              <div className="w-[min(100vw-2rem,520px)]">
                <CardComponent
                  item={activeItem}
                  index={activeItemIndex >= 0 ? activeItemIndex : 0}
                  isLoading={false}
                  onEdit={onEditRule ? () => onEditRule(activeItem) : undefined}
                  onDelete={onDeleteRule ? () => onDeleteRule(activeItem) : undefined}
                  isActive={activeItem.is_active}
                  onToggleActive={() => onToggleRule && onToggleRule(activeItem, !activeItem.is_active)}
                  isListReordering
                  isDragPreview
                />
              </div>
            ) : null}
          </DragOverlay>
        </>
      )}
    </DndContext>
  ) : fillContainer ? (
    <div className="min-h-0 flex-1">
      <VirtualList {...listSharedProps} />
    </div>
  ) : (
    <VirtualList {...listSharedProps} />
  );

  const reorderToolbar =
    isOnSave && canEdit && hasUnsavedOrder ? (
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-b border-border bg-muted/30 px-2 py-2 sm:px-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          disabled={reorderMutation.isPending}
          onClick={handleCancelOrder}
        >
          {t('clientList.reorder.cancel')}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          className="h-8 text-xs"
          disabled={reorderMutation.isPending}
          onClick={handleSaveOrder}
        >
          {reorderMutation.isPending ? t('clientList.reorder.saving') : t('clientList.reorder.save')}
        </Button>
      </div>
    ) : null;

  if (fillContainer) {
    return (
      <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col">
        {reorderToolbar}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">{inner}</div>
      </div>
    );
  }

  return (
    <>
      {reorderToolbar}
      {inner}
    </>
  );
};
